<template lang="pug">
    div(
        class="_pcirc",
        :class="{ _busy: busy }",
        :style="{ width, height }",
        :inert="blocking && busy")
        transition(name="_pcirct", :duration="1000")
            div(
                v-show="show && ready",
                class="_pcirco",
                role="progressbar",
                :aria-valuenow="value",
                :aria-valuemin="value !== undefined ? min : undefined",
                :aria-valuemax="value !== undefined ? max : undefined")
                svg(viewBox="0 0 64 64")
                    circle(
                        class="_pcircbg",
                        cx="32",
                        cy="32",
                        r="24")
                    circle(
                        class="_pcircfg",
                        :class="{ _inf: value === undefined }",
                        cx="32",
                        cy="32",
                        r="24",
                        stroke-dasharray="150.7968",
                        :stroke-dashoffset="dashoffset",
                        :style="{ 'transition-duration': duration + 'ms' }",
                        @transitionend="_done")
        slot
</template>

<script>
    /**
     * Progress indicator with circular (bar) style.
     */
    export default {
        name: 'ib-progress-circular',

        props: {
            /**
             * If `true`, block interaction with slot content unless not visible or `value == max`.
             */
            blocking: Boolean,
            /**
             * If `false`, hide the progress bar, but display slot content.
             */
            show: {
                type: Boolean,
                default: true
            },
            /**
             * Current value to display; clamped to [min,max] inclusive. If not specified, the
             * progress indicator will loop continually.
             */
            value: {
                type: Number,
                default: undefined
            },
            /**
             * Minimum value that can be displayed.
             */
            min: {
                type: Number,
                default: 0
            },
            /**
             * Maximum value that can be displayed.
             */
            max: {
                type: Number,
                default: 1
            },
            /**
             * Length of time to animate value change, in millseconds.
             */
            duration: {
                type: Number,
                default: 500
            },
            /**
             * Width in any CSS units.
             */
            width: String,
            /**
             * Height in any CSS units.
             */
            height: String
        },

        data() {
            return {
                ready: false,
                transition: false
            };
        },

        computed: {
            busy() {
                return this.show && (this.transition || this.value === undefined);
            },

            clamped() {
                return this.value === undefined ? undefined : Math.max(this.min, Math.min(this.max, this.value));
            },

            dashoffset() {
                if (this.clamped !== undefined) {
                    return 150.7968 * (1 - (this.clamped / (this.max - this.min)));
                } else {
                    return 150.7968 * 0.5;
                }
            }
        },

        watch: {
            clamped() {
                if (!this.duration) {
                    this._done();
                } else {
                    this.transition = true;
                }
            }
        },

        mounted() {
            // Ensure visibility transition is displayed on mount
            setTimeout(() => {
                this.ready = true;
            }, 96);
        },

        methods: {
            /** @private */
            _done() {
                this.transition = false;
                this.$emit('change', this.clamped);
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    @keyframes _pcins {
        from {
            opacity: 0;
        } to {
            opacity: 1;
        }
    }
    @keyframes _pcinf {
        0% {
            transform: rotate(0deg);
        } 100% {
            transform: rotate(360deg);
        }
    }

    .progress-circular-color(@color) {
        ._pcircfg {
            stroke: @color;
        }
    }

    ._pcirc {
        position: relative;

        &[inert] {
            &::before {
                content: '';
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
                background: fade(@bg, 60%);
                pointer-events: all;
                z-index: @z-progress;
                cursor: wait;
            }
        }

        & ._pcirco {
            visibility: visible;
            opacity: 1;
            transition: opacity 1s linear;
        } &._h ._pcirco {
            visibility: hidden;
            opacity: 0;
            transition: opacity .25s linear, visibility 0s linear .25s;
        }
    }
    ._pcirco {
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        pointer-events: none;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: @z-progress;

        svg {
            width: 96px;
            height: 96px;
            max-width: 100%;
            max-height: 100%;
            fill: transparent;
            stroke-width: 3px;
        }

        /* Color classes */
        &           { .progress-circular-color(@bg-highlight); }
        &.accent    { .progress-circular-color(@fg-accent); }
        &.positive  { .progress-circular-color(@fg-positive); }
        &.negative  { .progress-circular-color(@fg-negative); }
        &.buy       { .progress-circular-color(@fg-buy); }
        &.hold      { .progress-circular-color(@fg-hold); }
        &.sell      { .progress-circular-color(@fg-sell); }
        &.ok        { .progress-circular-color(@fg-ok); }
        &.warning   { .progress-circular-color(@fg-warning); }
        &.error     { .progress-circular-color(@fg-error); }
    }
    ._pcircbg {
        stroke: fade(mix(@fg-max, @bg, 8%), 80%);
    }
    ._pcircfg {
        transform-origin: center;
        transition: stroke-dashoffset;
        &._inf {
            animation: _pcinf 1s infinite;
        }
    }

    ._pcirct {
        &-enter-active {
            animation: _pcins 1s;
        }
        &-leave-active {
            animation: _pcins .25s reverse;
        }
    }
</style>

<docs>
    Basic Example:
    ```jsx
    <ib-context>
        <ib-progress-circular :min="0" :max="100" position="top" value="50" class="inset-md bg10-buy">
            <p>50% Done</p>
        </ib-progress-circular>
        <ib-progress-circular :min="0" :max="100" position="top" value="25" class="inset-md bg10-buy" position="background">
            <p>Prices as of 13:45:37 EDT</p>
        </ib-progress-circular>
    </ib-context>
    ```
</docs>
